import type { CommunityMember } from "@circle-react/types";
import { Typography } from "@circleco/compass/components/Typography";
import { format } from "date-fns";

import { InReviewBadge } from "@/react/components/Chat/ChatWindowV2/MessageItem/MessageBlock/InReviewBadge";
import { TipTapMessageBody } from "@/react/components/Chat/ChatWindowV2/MessageItem/TipTapMessageBody";
import { SenderAvatar } from "@/react/components/Chat/ChatWindowV2/MessageItemCompass/MessageBlock/SenderAvatar";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import type { Sender } from "@/react/types/Chat/ChatRoomMessage";

import type { FlaggedChatMessageRowProps } from "./types";

const formatMessageTime = (isoDate: string) =>
  format(new Date(isoDate), "h:mm a");

const communityMemberToSender = (author: CommunityMember): Sender => ({
  id: author.id,
  community_member_id: author.id,
  name: author.name ?? "",
  avatar_url: author.avatar_url,
  user_public_uid: author.public_uid,
});

export const FlaggedChatMessage = ({
  author,
  isFlagged = false,
  sentAt,
  tiptapBody,
}: FlaggedChatMessageRowProps) => {
  const memberProfileDrawer = useProfileDrawer();

  const authorName = author?.name ?? "";
  const sender = author ? communityMemberToSender(author) : undefined;

  const openProfileDrawer = () => {
    if (!sender?.user_public_uid) return;

    void memberProfileDrawer.show({
      memberPublicId: sender.user_public_uid,
    });
  };

  const content = (
    <div className="flex w-full justify-start">
      <SenderAvatar
        sender={sender}
        isLoading={false}
        onOpenProfile={openProfileDrawer}
      />
      <div className="min-w-0 w-full flex-1 overflow-x-auto">
        <div className="mb-px flex flex-wrap items-center gap-x-2 gap-y-1">
          <Typography variant="heading-sm">{authorName}</Typography>
          {sentAt && (
            <Typography variant="body-sm" color="disabled">
              {formatMessageTime(sentAt)}
            </Typography>
          )}
          {isFlagged && <InReviewBadge shouldShowTooltip={false} />}
        </div>
        <TipTapMessageBody
          rich_text_body={tiptapBody}
          shouldScrollIntoView={false}
          editedAt={null}
          chatRoomAccessArr={[]}
          shouldShowPreviewBlocks={false}
          chatProps={{
            unfurlUrls: [],
          }}
        />
      </div>
    </div>
  );

  if (isFlagged) {
    return <div className="rounded-lg bg-warning/10 p-2">{content}</div>;
  }
  return <div className="p-2">{content}</div>;
};
